import React from "react";
import { useSettings } from "../context/SettingsContext";

/**
 * Light, and deliberately short.
 *
 * This was briefly a tall dark-green panel. Two things were wrong with it: the
 * site is a light, papery thing from top to bottom, so a black-green slab at the
 * end read as a different website bolted on; and at that height it took up more
 * of the scroll than the content above it in places. It is now the same stone as
 * the rest of the page furniture, and about a third of the height.
 *
 * The navigation column that used to sit here is still gone: every one of those
 * links is already in the header, and repeating them was the clearest case of
 * the "linking is great, repeating is less ideal" note.
 */
export const Footer = () => {
  const year = new Date().getFullYear();
  const { settings: s } = useSettings();

  // Location, not a full postal address: enough for a visitor to place the
  // group, without implying the door is open for drop-ins. Postal code and
  // country are deliberately not rendered.
  const location = [
    s.org_short_name || "IWM Research Group",
    s.address_line1,
    s.address_line2,
    [s.city, s.region].filter(Boolean).join(", "),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    // No top margin. It used to carry mt-24, which was invisible while every
    // page ended on the same bone background — and then showed up as a pale
    // stripe the moment a page ended on a coloured panel, because a margin is
    // the parent's background, not the footer's. Every section already brings
    // its own bottom padding, so the separation was never needed.
    <footer className="border-t hairline bg-[#F2EFEA]" data-testid="site-footer">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
          <div className="lg:col-span-6">
            <h2 className="font-serif text-xl sm:text-2xl leading-snug text-[#1C2722] max-w-lg">
              {s.org_name || "Innovative Waste Management Research Group"}.
              <span className="text-[#4A5A52]"> Exploring the systems that turn waste into resources.</span>
            </h2>
            {s.affiliation && (
              <div className="mt-5 inline-flex items-center gap-2.5">
                <span className="w-2 h-2 bg-[#A17E3B] shrink-0" />
                <span className="text-[12px] font-mono tracking-widest uppercase text-[#1C2722]" data-testid="footer-affiliation">
                  {s.affiliation}
                </span>
              </div>
            )}
          </div>

          <div className="lg:col-span-3">
            <div className="overline mb-3">Location</div>
            <address className="not-italic text-[13.5px] leading-relaxed text-[#4A5A52]" data-testid="footer-address">
              {location}
            </address>
          </div>

          {s.email && (
            <div className="lg:col-span-3">
              <div className="overline mb-3">Email</div>
              <div className="text-[15px] text-[#1C2722] select-all break-words" data-testid="footer-email">
                {s.email}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="border-t hairline">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-[11px] font-mono tracking-widest uppercase text-[#62706A]">
          <div>© {year} · {s.org_name || "IWM Research Group"}</div>
          <div>{[s.city, s.region].filter(Boolean).join(" · ")}</div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
